/* eslint-disable react/prop-types */

/**
 * ProductDetailAttrProvider
 * Contain most logic handing with Omnitech Api
 */
import _ from 'lodash'
import React, { useContext, useMemo, useState, useEffect, useRef } from 'react'
import { cancelRequest } from '../../helpers'
import { CacheChecksumContext, createPersistedState } from '../../cache'
import ProductDetailAttrContext from './product-detail-attr-context'
import { fetchProductDetailAttributeOptions } from './product-detail-attr-api'

// create use state hook with persisted storage
// TODO: support using different storage provider
const useProductDetailAttributeOptionsState = createPersistedState('productDetailAttributeOptions')
const useProductDetailAttributeOptionsChecksumState = createPersistedState('productDetailAttributeOptionsChecksum')

export default function ProductDetailAttrProvider({ children, config, currentLocale }) {
  // Ref
  const tempCacheRef = useRef([])

  // handle cache checksum value
  const { getChecksum } = useContext(CacheChecksumContext)
  const cacheChecksumValue = getChecksum('productDetailAttributeOptions')
  // internal state
  const [productDetailAttributeOptions, setProductDetailAttributeOptions] = useProductDetailAttributeOptionsState([])
  const [tempCache, setTempCache] = useState([])
  const [productDetailAttributeOptionsChecksum, setProductDetailAttributeOptionsChecksum] = useProductDetailAttributeOptionsChecksumState('')
  const [productDetailAttrReady, setProductDetailAttrReady] = useState(false)
  const [enableRefresh, setEnableRefresh] = useState(false)

  // get cache with current locale
  const availableLocales = useMemo(() => (
    _.get(config, 'locale.availableLocales')
  ), [config])
  const currentLocaleData = useMemo(() => (
    _.get(productDetailAttributeOptions, currentLocale, [])
  ), [currentLocale, productDetailAttributeOptions])
  const currentLocaleChecksum = useMemo(() => (
    _.get(productDetailAttributeOptionsChecksum, currentLocale, '')
  ), [currentLocale, productDetailAttributeOptionsChecksum])

  // use reference for temp cache
  tempCacheRef.current = tempCache

  /**
   * apiFetchCategories
   * api call to fetch category
   */
  function apiFetchProductDetailAttr(page = 1) {
    return fetchProductDetailAttributeOptions({
      includes: [

      ].join(','),
      page
    })
  }

  /**
   * handleFetchNext
   * handle to call next page of categories data
   * @param {*} nextCB
   * @param {*} endCB
   */
  async function handleFetchNext(nextCB, endCB) {
    // if next is not a function, trigger end callback directly
    if (!_.isFunction(nextCB)) {
      setProductDetailAttributeOptions({
        ..._.pick(productDetailAttributeOptions, availableLocales),
        [currentLocale]: [
          ...(tempCacheRef.current || [])
        ]
      })
      if (_.isFunction(endCB)) endCB()
      return
    }

    try {
      const { productDetailAttributeOptions: data, next } = await nextCB()
      if (_.isFunction(next)) {
        setTempCache([
          ...(tempCacheRef.current || []),
          ...data
        ])
        handleFetchNext(next, endCB)
      } else {
        // once all pages is fetched, update the actual state used
        setProductDetailAttributeOptions({
          ..._.pick(productDetailAttributeOptions, availableLocales),
          [currentLocale]: [
            ...(tempCacheRef.current || []),
            ...data
          ]
        })
        if (_.isFunction(endCB)) endCB()
      }
    } catch (error) {
      console.warn('fail to load productDetailAttr for cache: ', error)
    }
  }

  /**
   * handleFetchCategories
   * get categories for caching, will be used for making menu
   */
  async function handleFetchProductDetailAttr() {
    // use categories in cache first
    if (
      !_.isEmpty(currentLocaleData) &&
      currentLocaleChecksum === cacheChecksumValue
    ) {
      setProductDetailAttrReady(true)
      setEnableRefresh(true)
      return
    }

    try {
      setEnableRefresh(false)
      const { cacheChecksum, productDetailAttributeOptions: data, next } = await apiFetchProductDetailAttr()
      setTempCache(data)
      handleFetchNext(next, () => {
        setProductDetailAttrReady(true)
        setEnableRefresh(true)
        setProductDetailAttributeOptionsChecksum({
          ..._.pick(productDetailAttributeOptionsChecksum, availableLocales),
          [currentLocale]: cacheChecksum
        })
      })
    } catch (error) {
      setEnableRefresh(true)
      console.warn('fail to load Product Detail Attributes  for cache: ', error)
    }
  }

  /**
   * handleRefreshProductDetailAttrCache
   * refresh departments cache when checksum is changed
   */
  async function handleRefreshProductDetailAttrCache() {
    // when it is not required to update cache, skip the process
    if (!enableRefresh || currentLocaleChecksum === cacheChecksumValue) {
      if (_.isEmpty(currentLocaleChecksum) && !_.isEmpty(cacheChecksumValue)) {
        setProductDetailAttributeOptionsChecksum({
          ..._.pick(productDetailAttributeOptionsChecksum, availableLocales),
          [currentLocale]: cacheChecksumValue
        })
      }
      return
    }

    try {
      // start refreshing cache data
      setEnableRefresh(false)
      const { cacheChecksum, categories: data, next } = await apiFetchProductDetailAttr()
      setTempCache(data)
      handleFetchNext(next, () => {
        setEnableRefresh(true)
        setProductDetailAttributeOptionsChecksum({
          ..._.pick(productDetailAttributeOptionsChecksum, availableLocales),
          [currentLocale]: cacheChecksum
        })
      })
    } catch (error) {
      setEnableRefresh(true)
      console.warn('fail to refresh Product Detail Attributes for cache', error)
    }
  }

  /**
   * get all categories from omnitech API when app is mounted
   */
  useEffect(() => {
    handleFetchProductDetailAttr()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchProductDetailAttributeOptions'])
    }
  }, [])

  /**
   * update categories cache base on cache checksum
   */
  useEffect(() => {
    if (!productDetailAttrReady || _.isUndefined(cacheChecksumValue)) return
    handleRefreshProductDetailAttrCache()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchProductDetailAttributeOptions'])
    }
  }, [cacheChecksumValue, productDetailAttrReady, currentLocaleChecksum])

  const state = {
    productDetailAttributeOptions: currentLocaleData,
    productDetailAttrReady,
    fetchProductDetailAttributeOptions
  }

  return (
    <ProductDetailAttrContext.Provider value={state}>
      {children}
    </ProductDetailAttrContext.Provider>
  )
}
